/**
 * Package Details Modal
 * Displays comprehensive package information in a modal popup
 */
export class PackageDetailsModal {
  constructor() {
    this.modal = null;
    this.overlay = null;
    this.init();
  }

  init() {
    this.createModal();
    this.bindEvents();
  }

  createModal() {
    // Create modal overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'package-modal-overlay';
    this.overlay.innerHTML = `
      <div class="package-modal">
        <div class="package-modal-header">
          <h2 class="package-modal-title"></h2>
          <button class="package-modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="package-modal-body">
          <div class="package-modal-content">
            <!-- Content will be dynamically populated -->
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlay);
    this.modal = this.overlay.querySelector('.package-modal');
  }

  bindEvents() {
    // Close modal when clicking overlay or close button
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    this.overlay.querySelector('.package-modal-close').addEventListener('click', () => {
      this.close();
    });

    // Close modal with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('active')) {
        this.close();
      }
    });
  }

  show(packageData) {
    const content = this.overlay.querySelector('.package-modal-content');
    const title = this.overlay.querySelector('.package-modal-title');

    title.textContent = packageData.title;
    content.innerHTML = this.generateModalContent(packageData);

    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  generateModalContent(pkg) {
    return `
      <div class="modal-section">
        <div class="modal-section-header">
          <h3><i class="fas fa-info-circle"></i> Package Overview</h3>
        </div>
        <div class="modal-grid">
          <div class="modal-info-item">
            <span class="label">Duration:</span>
            <span class="value">${pkg.duration_days} Days / ${pkg.nights}</span>
          </div>
          <div class="modal-info-item">
            <span class="label">Star Rating:</span>
            <span class="value">
              ${'<i class="fas fa-star"></i>'.repeat(pkg.star_rating)}
              ${'<i class="far fa-star"></i>'.repeat(5 - pkg.star_rating)}
            </span>
          </div>
          <div class="modal-info-item">
            <span class="label">Travel Dates:</span>
            <span class="value">${this.formatDate(pkg.start_date)} - ${this.formatDate(pkg.end_date)}</span>
          </div>
          <div class="modal-info-item">
            <span class="label">Route:</span>
            <span class="value">${pkg.dep_airport} → ${pkg.arr_airport}</span>
          </div>
        </div>
      </div>

      ${this.generateHotelsSection(pkg.hotels)}
      ${this.generatePricingSection(pkg.pricing)}
      ${this.generateFlightsSection(pkg.flights)}
      ${this.generateInclusionsSection(pkg.inclusions)}

      <div class="modal-actions">
        <button class="btn btn-primary btn-large" onclick="window.location.href='/contact.html'">
          <i class="fas fa-envelope"></i> Enquire Now
        </button>
        <button class="btn btn-secondary btn-large" onclick="closeModalAndNavigate('/#calendly-widget')">
          <i class="fas fa-calendar-check"></i> Book Now
        </button>
      </div>
    `;
  }

  generateHotelsSection(hotels) {
    if (!hotels || hotels.length === 0) return '';

    return `
      <div class="modal-section">
        <div class="modal-section-header">
          <h3><i class="fas fa-hotel"></i> Accommodation</h3>
        </div>
        <div class="hotels-grid">
          ${hotels.map(hotel => `
            <div class="hotel-card">
              <div class="hotel-info">
                <h4>${hotel.hotel_name}</h4>
                <p class="hotel-location">
                  <i class="fas fa-map-marker-alt"></i> ${hotel.city}
                  ${hotel.distance_m ? `<span class="distance">${hotel.distance_m} from ${hotel.distance_ref}</span>` : ''}
                </p>
                <p class="hotel-nights">
                  <i class="fas fa-bed"></i> ${hotel.nights} Night${hotel.nights !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  generatePricingSection(pricing) {
    if (!pricing || pricing.length === 0) return '';

    return `
      <div class="modal-section">
        <div class="modal-section-header">
          <h3><i class="fas fa-pound-sign"></i> Pricing Options</h3>
        </div>
        <div class="pricing-table">
          <div class="pricing-header">
            <span>Room Type</span>
            <span>Price (PKR)</span>
          </div>
          ${pricing.map(price => `
            <div class="pricing-row">
              <span class="room-type">${price.room_type}</span>
              <span class="price-amount">₨${price.price.toLocaleString()}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  generateFlightsSection(flights) {
    if (!flights || (!flights.outbound?.length && !flights.return?.length)) return '';

    return `
      <div class="modal-section">
        <div class="modal-section-header">
          <h3><i class="fas fa-plane"></i> Flight Information</h3>
        </div>
        <div class="flights-container">
          ${flights.outbound?.length ? this.generateFlightLeg('Outbound', flights.outbound) : ''}
          ${flights.return?.length ? this.generateFlightLeg('Return', flights.return) : ''}
        </div>
      </div>
    `;
  }

  generateFlightLeg(legType, flightData) {
    return `
      <div class="flight-leg">
        <h4 class="flight-leg-title">
          <i class="fas fa-plane-departure"></i> ${legType} Flights
        </h4>
        ${flightData.map(flight => `
          <div class="flight-item">
            <div class="flight-header">
              <span class="flight-number">${flight.flight_no}</span>
              <span class="flight-class">${flight.booking_class}</span>
            </div>
            <div class="flight-route">
              <div class="flight-point">
                <span class="airport-code">${flight.dep_code}</span>
                <span class="flight-time">${this.formatTime(flight.dep_time)}</span>
              </div>
              <div class="flight-arrow">
                <i class="fas fa-arrow-right"></i>
              </div>
              <div class="flight-point">
                <span class="airport-code">${flight.arr_code}</span>
                <span class="flight-time">${this.formatTime(flight.arr_time)}</span>
              </div>
            </div>
            <div class="flight-date">
              <i class="fas fa-calendar"></i> ${this.formatDate(flight.date)}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  generateInclusionsSection(inclusions) {
    if (!inclusions || inclusions.length === 0) return '';

    return `
      <div class="modal-section">
        <div class="modal-section-header">
          <h3><i class="fas fa-check-circle"></i> Package Inclusions</h3>
        </div>
        <div class="inclusions-grid">
          ${inclusions.map(inclusion => `
            <div class="inclusion-item">
              <i class="fas fa-check"></i>
              <span>${inclusion}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  formatDate(dateString) {
    if (!dateString) return '';
    
    try {
      // Convert to string if it's not already
      const stringDate = String(dateString);
      
      // Normalize various Unicode dash characters to regular hyphens
      const normalizedDate = stringDate
        .replace(/[\u2010-\u2015\u2212]/g, '-') // Various Unicode dashes
        .replace(/\u2013/g, '-') // En dash
        .replace(/\u2014/g, '-') // Em dash
        .replace(/‑/g, '-') // Non-breaking hyphen
        .trim();
      
      // Parse the date - try multiple formats
      let date;
      
      // If it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
        date = new Date(normalizedDate + 'T00:00:00');
      } else {
        // Try parsing as-is
        date = new Date(normalizedDate);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString, 'normalized to:', normalizedDate);
        return dateString;
      }
      
      // Return a compact but readable format: DD MMM YYYY
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'dateString:', dateString);
      return dateString;
    }
  }

  formatTime(timeString) {
    if (!timeString) return '';
    
    try {
      // Normalize the time string
      let normalizedTime = String(timeString).trim();
      
      // If it's already in HH:MM format, use it directly
      if (/^\d{1,2}:\d{2}$/.test(normalizedTime)) {
        // Ensure it's in HH:MM format (pad single digit hours)
        const parts = normalizedTime.split(':');
        const hours = parts[0].padStart(2, '0');
        const minutes = parts[1];
        return `${hours}:${minutes}`;
      }
      
      // If it's in H:MM format (single digit hour), pad it
      if (/^\d:\d{2}$/.test(normalizedTime)) {
        return '0' + normalizedTime;
      }
      
      // Try to parse as a Date object for more complex formats
      const time = new Date(`2000-01-01T${normalizedTime}`);
      if (!isNaN(time.getTime())) {
        return time.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
      
      // If all else fails, return the original string
      return timeString;
    } catch (error) {
      console.warn('Error formatting time:', error, 'timeString:', timeString);
      return timeString;
    }
  }
}

// Export singleton instance
export const packageDetailsModal = new PackageDetailsModal();

// Global function to close modal and navigate
window.closeModalAndNavigate = function(url) {
  packageDetailsModal.close();
  setTimeout(() => {
    window.location.href = url;
  }, 300); // Small delay to allow modal close animation
};
